import { FC, useEffect, useState, useMemo } from 'react';
import { DashboardViewProps } from './dashboard.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import MainTitle from '../../components/mainTitle';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { DashboardProvider } from './contexts/dashboard.context';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import TabGroup from 'src/views/components/tabGroup';
import { useDashboardTabMenuItems } from './components/dashboardTabMenuItems.component';
import { useRbacState } from 'src/context/rbac.context';
import { hasReadPermission } from 'src/utils/rbacHelpers';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';

// Create a separate component for the content
const DashboardContent: FC = () => {
  const { translateText } = useTranslation();
  const { dashboardSubRouteId } = useParams();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const dashboardTabs = useDashboardTabMenuItems(translateText);
  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();

  const availableRouteIds = useMemo(() => {
    const routes: string[] = [];
    if (hasReadPermission(rbac, 'dashboard', 'finance')) {
      routes.push('finance-overview');
    }
    if (hasReadPermission(rbac, 'dashboard', 'people')) {
      routes.push('people');
    }
    if (hasReadPermission(rbac, 'dashboard', 'events')) {
      routes.push('events');
    }
    return routes;
  }, [rbac]);

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      const routeId = availableRouteIds[tabIndex];
      navigate(pages.dashboard.path + '/' + routeId, {
        replace: true,
      });
    }
  };

  useEffect(() => {
    if (availableRouteIds.length === 0) {
      return;
    }
    const routeId =
      dashboardSubRouteId && dashboardSubRouteId !== 'undefined'
        ? dashboardSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.dashboard.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const filteredTabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(filteredTabIndex >= 0 ? filteredTabIndex : 0);

    if (!dashboardSubRouteId || dashboardSubRouteId === 'undefined') {
      navigate(pages.dashboard.path + '/' + routeId, {
        replace: true,
      });
    }
  }, [dashboardSubRouteId, navigate, availableRouteIds]);

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  return (
    <Grid
      container
      data-testid="dashboard-content-container"
      sx={{ height: '100%' }}
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          pr: { xs: 0, sm: 4 },
          gap: { xs: 1, sm: 0 },
          mb: { xs: 1, sm: 0 },
          height: 'var(--main-title-height)',
        }}
        data-testid="dashboard-view-header-container"
      >
        <MainTitle title={translateText('dashboard.title')}
          subTitle={selectedTab ? dashboardTabs[selectedTab].label : ''}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sx={{ height: '100%' }}
      >
        <TabGroup
          tabs={dashboardTabs}
          initiallySelected={selectedTab}
          onTabChange={(value) => locationChangeHandler(value)}
          dataTestIdPrefix="dashboard"
        />
      </Grid>
    </Grid>
  );
};

// Main component that provides the context
const DashboardView: FC<DashboardViewProps> = () => {
  return (
    <NavigationDrawer
      selectedPage={pages.dashboard.path}
      window={window ?? null}
    >
      <Helmet>
        <title>{CONSTANTS.title.dashboard}</title>
      </Helmet>
      <DashboardProvider>
        <DashboardContent />
      </DashboardProvider>
    </NavigationDrawer>
  );
};

export default DashboardView;
